import _ from "lodash";

import ACLStore from "lib/aclStore";
import katex from "katex";
import { generateRandomId, htmlToText } from "Utils";
import { uploadFileV2 } from "modules/Services";
import { convertMathToBlob } from "UIComponents/MathEquationEditor/mathFontManager";
import { LATEX_DELIMITER_REGEX, LATEX_COMMAND_REGEX } from "./Constants";
import { wrapPlaceholderWithSpan } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/components/PromptCards/PromptCards";

const isPlaceholderSpanSame = (originalHTML, modifiedHTML) => {
  const originalContainer = document.createElement("div");
  const modifiedContainer = document.createElement("div");

  originalContainer.innerHTML = originalHTML;
  modifiedContainer.innerHTML = modifiedHTML;

  const originalSpans = originalContainer.querySelectorAll(
    "#content-editable-placeholder"
  );
  const modifiedSpans = modifiedContainer.querySelectorAll(
    "#content-editable-placeholder"
  );

  if (_.size(originalSpans) !== _.size(modifiedSpans)) return false;

  const isPlaceholderContentSame = _.some(originalSpans, (span, idx) => {
    const originalPlaceholderText = span?.textContent?.trim();
    const modifiedPlaceholderText = modifiedSpans?.[idx]?.textContent?.trim();

    return (
      originalPlaceholderText === modifiedPlaceholderText ||
      _.isEmpty(modifiedPlaceholderText)
    );
  });

  // Check for attachment content changes
  const originalAttachmentContent = originalContainer.querySelector(
    "#attachment-content"
  );
  const modifiedAttachmentContent = modifiedContainer.querySelector(
    "#attachment-content"
  );

  const hasAttachmentContentChanged =
    originalAttachmentContent?.textContent !==
    modifiedAttachmentContent?.textContent;

  return (
    isPlaceholderContentSame ||
    (originalAttachmentContent && !hasAttachmentContentChanged)
  );
};

export const getIsSendButtonDisabled = params => {
  const {
    value,
    isAITutor,
    selectedAction,
    isAttachmentUploading,
    attachments,
    isSpeakingOnly,
  } = params;

  // empty spaces, new lines are represented as "&nbsp;", "<br>", "\n" in contenteditable
  // so simple trim will not work, hence we need to convert the html to text and then trim
  const isQueryEmpty = _.isEmpty(
    _.trim(
      htmlToText(value, {
        preserveNewlines: false,
        wordwrap: null,
      })
    )
  );

  const isAttachmentEmpty = _.size(attachments) === 0;

  const isEditorEmpty =
    !isAttachmentEmpty &&
    _.every(attachments, attachment => {
      const attachmentContent = _.trim(
        htmlToText(attachment?.modalityAttachment?.content, {})
      );

      return (
        _.includes(["TODDLE_DOC", "CODE_EDITOR"], attachment?.type) &&
        _.isEmpty(attachmentContent)
      );
    });

  return (
    (_.isEmpty(selectedAction) &&
      isQueryEmpty &&
      isEditorEmpty &&
      !isSpeakingOnly) || // if no prompt is selected or no input has been given
    isAttachmentUploading || // if attachment is uploading
    (isAITutor &&
      !_.isEmpty(selectedAction) &&
      isPlaceholderSpanSame(selectedAction?.label, value))
  );
};

export const moveCaretToEnd = ref => {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(ref);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const moveCaretToEndOfContentEditable = ref => {
  if (!ref) return;

  setTimeout(() => {
    const editableSpans = ref.querySelectorAll("span[contenteditable='true']");
    const lastEditableSpan = _.first(editableSpans); // focus on first editable span

    if (lastEditableSpan) {
      const range = document.createRange();
      const selection = window.getSelection();

      // Move the caret to the end of the contentEditable span
      range.selectNodeContents(lastEditableSpan);
      range.collapse(false); // Collapses the range to the end of the node
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      moveCaretToEnd(ref);
    }
  }, 0);
};

export const getPromptLabelWithAttachments = ({
  t,
  promptLabel,
  attachments,
}) => {
  if (_.isEmpty(attachments)) return promptLabel;

  // remove initial 'insert attachment' btn
  const tempElement = document.createElement("div");
  tempElement.innerHTML = promptLabel;
  const attachmentElement = tempElement.querySelector("#attachment-text");
  if (!attachmentElement) return promptLabel;

  const attachmentsInSpan = _.reduce(
    attachments,
    (updatedLabel, attachment, index) => {
      return wrapPlaceholderWithSpan({
        t,
        label: `${updatedLabel} ##${attachment.name}##`,
        attachmentId: attachment.id,
        showEditIcon: true,
        removeRightMargin: index === _.size(attachments) - 1,
      });
    },
    ""
  );

  // Replace the entire attachmentElement with the new HTML content
  attachmentElement.outerHTML = attachmentsInSpan;

  return tempElement.innerHTML;
};

export const shouldRemoveBrTagFromEnd = ({ selectedAction, ref }) => {
  const childNodes = ref?.childNodes;
  /**
   * On space key press, a br tag is appended at the end in firefox browser
   * when there is no selectedAction, length of childNodes is 2 and last child is br tag if space is given
   * So, _.isEmpty(selectedAction) is required, otherwise space will be removed
   */
  return (
    !_.isEmpty(selectedAction) &&
    _.size(childNodes) === 2 &&
    _.last(ref.childNodes)?.nodeName === "BR"
  );
};

export const cleanLabel = label => {
  if (!_.isString(label)) return label;

  return label
    .replace(/\s*(-\s*)?\{\{[^}]*\}\}?\s*/g, " ")
    .replace(/\s*-\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
};

export const getPlaceholder = params => {
  const { t, value, selectedAction, isAITutor, isInitialState } = params;

  const trimmedQuery = _.trim(value);

  if (
    (_.isEmpty(selectedAction) && _.isEmpty(trimmedQuery)) ||
    _.isEmpty(trimmedQuery)
  ) {
    if (isAITutor) {
      if (isInitialState)
        return t("ai:start_by_defining_your_tutoring_objective_placeholder");
      else return t("ai:ai_tutor_chat_placeholder");
    } else return t("classRoom:start_typing_to_get_suggestions");
  } else {
    if (isAITutor) return t("ai:ai_tutor_prompt_selected_placeholder");
    else return t("ai:le_prompt_selected_placeholder");
  }
};

export const isOnlySpanTag = inputString => {
  const regex = /^\s*<span\b[^>]*>([\s\S]*?)<\/span>(\s|&nbsp;)?$/;
  return regex.test(inputString);
};

export const processClipboardFiles = async files => {
  const processedFiles = [];

  for (const file of files) {
    // Skip if not a valid file
    if (!file || !file.type) continue;

    try {
      const processedFile = {
        name: file.name,
        type: file.type,
        size: file.size,
        sizeBytes: file.size,
        url: URL.createObjectURL(file),
        file: file,
        mimeType: file.type,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      };

      processedFiles.push(processedFile);
    } catch (error) {
      console.error("Error processing clipboard file:", file.name, error);
    }
  }

  return processedFiles;
};

export const getClipboardFiles = clipboardData => {
  const files = Array.from(clipboardData.files || []);

  const supportedImageTypes = [
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
  ];
  const supportedFileTypes = [
    ...supportedImageTypes,
    "application/pdf",
    "text/plain",
    "text/csv",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const supportedFiles = _.filter(files, file =>
    _.includes(supportedFileTypes, file.type)
  );

  return {
    clipboardFiles: supportedFiles,
  };
};

const getFileExtension = file => {
  const fileNameParts = file.split(".");
  return fileNameParts.length > 1 ? fileNameParts.pop() : "";
};

export const handleFileUploadAndAdd = ({
  file,
  parentId,
  attachmentsToAdd = _.noop,
}) => {
  return async dispatch => {
    try {
      const uploadId = generateRandomId();
      const fileName = _.get(file, "name", "pasted-image.png");
      const mimeType = _.get(file, "type", "image/png");
      const fileSize = _.get(file, "size", 0);

      // Validate file has actual content
      if (fileSize === 0) {
        console.error("File size is 0, cannot upload empty file");
        return;
      }

      // Get the actual File/Blob object - crucial for S3 upload
      const fileObject = _.get(file, "file") || file;

      if (!fileObject) {
        console.error("No file object found");
        return;
      }

      const attachment = {
        name: fileName,
        mimeType,
        metadata: {
          fileExtension: getFileExtension(fileName),
        },
      };

      // Call uploadFileV2 - it's a prop from mapActionCreators
      const uploadedUrl = await dispatch(
        uploadFileV2({
          file: fileObject, // Pass the actual File/Blob object, not metadata
          uploadId,
          attachment,
          parentType: "AI_CONVERSATION",
          parentId,
          portalType: "PLANNER",
        })
      );

      if (uploadedUrl) {
        attachmentsToAdd.push({
          id: generateRandomId(),
          type: mimeType.startsWith("image/") ? "IMAGE" : "FILE",
          name: fileName,
          url: uploadedUrl,
          mimeType,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const replaceMathEquation = params => {
  const { value = "", newEquation = "", targetDataId } = params;

  if (!_.trim(value)) return newEquation;

  const regex = new RegExp(
    `<img\\s+[^>]*data-id="${targetDataId}"([^>]*)\\/?>`,
    "g"
  );

  const updatedValue = _.replace(value, regex, newEquation);

  if (updatedValue === value) return `${value}${newEquation}`;

  return updatedValue;
};

export const checkIsSTTLanguageEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableSTTLanguageInput");
};

export const removeHtmlWithContent = html => {
  const container = document.createElement("div");
  container.innerHTML = html;

  const editableSpan = container.querySelector("#user-editable");

  return editableSpan ? editableSpan.innerHTML : "";
};

const stripLatexDelimiters = text => {
  const trimmedText = _.trim(text);

  if (_.isEmpty(trimmedText)) return trimmedText;

  if (_.startsWith(trimmedText, "$$") && _.endsWith(trimmedText, "$$"))
    return trimmedText.slice(2, -2);
  if (_.startsWith(trimmedText, "$") && _.endsWith(trimmedText, "$"))
    return trimmedText.slice(1, -1);
  if (_.startsWith(trimmedText, "\\(") && _.endsWith(trimmedText, "\\)"))
    return trimmedText.slice(2, -2);
  if (_.startsWith(trimmedText, "\\[") && _.endsWith(trimmedText, "\\]"))
    return trimmedText.slice(2, -2);

  return trimmedText;
};

export const isLikelyLatexText = text => {
  const trimmedText = _.trim(text);

  if (_.isEmpty(trimmedText)) return false;

  const hasLatexMarkers =
    LATEX_DELIMITER_REGEX.test(trimmedText) ||
    LATEX_COMMAND_REGEX.test(trimmedText);

  if (!hasLatexMarkers) return false;

  const normalizedLatex = stripLatexDelimiters(trimmedText);

  try {
    katex.renderToString(normalizedLatex, { throwOnError: true });
    return true;
  } catch (error) {
    return false;
  }
};

export const createLatexImageFromText = async text => {
  const normalizedLatex = stripLatexDelimiters(text);

  let latexHtml;

  try {
    latexHtml = katex.renderToString(normalizedLatex, {
      throwOnError: true,
    });
  } catch (error) {
    return null;
  }

  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  container.style.padding = "4px";
  container.innerHTML = latexHtml;

  document.body.appendChild(container);

  const katexNode = container.querySelector(".katex");

  if (!katexNode) {
    document.body.removeChild(container);
    return null;
  }

  const katexRect = katexNode.getBoundingClientRect();
  const strutRect = katexNode.querySelector(".strut")?.getBoundingClientRect();

  const verticalAlign = strutRect ? -(katexRect.bottom - strutRect.bottom) : 0;

  try {
    const blob = await convertMathToBlob(katexNode, {
      height: katexRect.height,
      width: katexRect.width,
      backgroundColor: null,
      pixelRatio: 4,
    });

    document.body.removeChild(container);

    if (!blob) return null;

    return {
      src: URL.createObjectURL(blob),
      height: Number(katexRect.height.toFixed(2)),
      width: Number(katexRect.width.toFixed(2)),
      latex: normalizedLatex,
      verticalAlign: Number(verticalAlign.toFixed(2)),
    };
  } catch (error) {
    document.body.removeChild(container);
    return null;
  }
};
